import { BINS, BIN_ORDER, type BinId } from '../data/bins';
import type { WasteCard } from '../data/cards';
import { subTopicLabels } from './subtopicLabels';

const GENERIC_HINT = '힌트: 이 물건이 어떤 재질로 만들어졌는지 다시 살펴보세요.';

/** Full-screen choose -> hint -> reveal flow for a single scanned card. */
export class AnswerCard {
  private readonly root = this.getElement('#answer-card');
  private readonly icon = this.getElement<HTMLImageElement>('#answer-icon');
  private readonly name = this.getElement('#answer-name');
  private readonly chooseSection = this.getElement('#answer-choose');
  private readonly binButtons = this.getElement('#bin-buttons');
  private readonly binFallbackToggle = this.getElement('#bin-fallback-toggle');
  private readonly hintSection = this.getElement('#answer-hint');
  private readonly hintTextEl = this.getElement('#answer-hint-text');
  private readonly hintRetryButton = this.getElement('#answer-hint-retry');
  private readonly revealSection = this.getElement('#answer-reveal');
  private readonly resultEl = this.getElement('#answer-result');
  private readonly comboEl = this.getElement('#answer-combo');
  private readonly hintUsedEl = this.getElement('#answer-hint-used');
  private readonly explanationEl = this.getElement('#answer-explanation');
  private readonly mistakeEl = this.getElement('#answer-mistake-note');
  private readonly subtopicsEl = this.getElement('#answer-subtopics');
  private readonly nextButton = this.getElement('#answer-next');

  constructor(onChoose: (bin: BinId) => void, onNext: () => void, onHintRetry: () => void) {
    for (const binId of BIN_ORDER) {
      const bin = BINS[binId];
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bin-button';
      button.style.setProperty('--bin-color', bin.color);
      button.textContent = bin.labelKo;
      button.addEventListener('click', () => onChoose(binId));
      this.binButtons.append(button);
    }
    this.nextButton.addEventListener('click', onNext);
    this.hintRetryButton.addEventListener('click', onHintRetry);
    this.binFallbackToggle.addEventListener('click', () => {
      this.binButtons.hidden = !this.binButtons.hidden;
    });
  }

  showChoose(card: WasteCard): void {
    this.icon.src = card.iconRef;
    this.icon.alt = card.nameKo;
    this.name.textContent = card.nameKo;
    this.chooseSection.hidden = false;
    this.hintSection.hidden = true;
    this.revealSection.hidden = true;
    this.root.hidden = false;
  }

  showHint(card: WasteCard): void {
    this.hintTextEl.textContent = card.commonMistakeNote ?? GENERIC_HINT;
    this.chooseSection.hidden = true;
    this.hintSection.hidden = false;
    this.revealSection.hidden = true;
  }

  showReveal(card: WasteCard, correct: boolean, comboBonus: number, hintUsed: boolean): void {
    this.chooseSection.hidden = true;
    this.hintSection.hidden = true;
    this.revealSection.hidden = false;
    this.resultEl.textContent = correct
      ? '정답이에요! 🎉'
      : `아쉬워요! 정답은 "${BINS[card.correctBin].labelKo}"예요.`;
    this.resultEl.className = correct ? 'correct' : 'incorrect';

    if (correct && comboBonus > 0) {
      this.comboEl.textContent = `🔥 콤보 보너스 +${comboBonus}!`;
      this.comboEl.hidden = false;
    } else {
      this.comboEl.hidden = true;
    }

    this.hintUsedEl.textContent = hintUsed ? '힌트를 보고 다시 도전했어요.' : '';
    this.hintUsedEl.hidden = !hintUsed;

    this.explanationEl.textContent = card.explanation;
    this.mistakeEl.textContent = !correct && card.commonMistakeNote ? card.commonMistakeNote : '';
    this.subtopicsEl.textContent = subTopicLabels(card.subTopics);
  }

  hide(): void {
    this.root.hidden = true;
  }

  private getElement<T extends HTMLElement = HTMLElement>(selector: string): T {
    const element = document.querySelector<T>(selector);
    if (!element) throw new Error(`Missing element: ${selector}`);
    return element;
  }
}
